import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';
import { buildBase, overlay, getAt, filterVisible } from '@/lib/content';

export const LANGS = {
  ku: { name: 'کوردی', dir: 'rtl' },
  ar: { name: 'العربية', dir: 'rtl' },
  en: { name: 'EN', dir: 'ltr' },
};

const LanguageContext = createContext(null);
const BASE = buildBase();
export const TOKEN_KEY = 'wiyak-admin-token';
export const PREVIEW_KEY = 'wiyak-preview';

function detectPreview() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('preview') === '1') localStorage.setItem(PREVIEW_KEY, '1');
  if (params.get('preview') === '0') localStorage.removeItem(PREVIEW_KEY);
  return localStorage.getItem(PREVIEW_KEY) === '1' && !!localStorage.getItem(TOKEN_KEY);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('wiyak-lang');
    return saved && LANGS[saved] ? saved : 'ku';
  });
  const [remote, setRemote] = useState(null);
  const [ready, setReady] = useState(false);
  const [preview] = useState(detectPreview);

  useEffect(() => {
    const url = preview ? `${API}/content/draft` : `${API}/content`;
    const cfg = preview ? { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } } : {};
    axios.get(url, cfg)
      .then(({ data }) => setRemote(preview ? data.draft : data.data))
      .catch(() => setRemote(null))
      .finally(() => setReady(true));
  }, [preview]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = LANGS[lang].dir;
    localStorage.setItem('wiyak-lang', lang);
  }, [lang]);

  const content = useMemo(() => ({
    ar: overlay(BASE.ar, remote?.ar),
    en: overlay(BASE.en, remote?.en),
    ku: overlay(BASE.ku, remote?.ku),
  }), [remote]);

  const t = useCallback((path, vars) => {
    let val = getAt(content[lang], path);
    if (val === undefined || val === '') val = getAt(content.en, path);
    if (val === undefined) return path;
    if (typeof val === 'string' && vars) {
      Object.entries(vars).forEach(([k, v]) => { val = val.replace(`{{${k}}}`, v); });
    }
    return filterVisible(val);
  }, [lang, content]);

  const dir = LANGS[lang].dir;
  const isRTL = dir === 'rtl';

  const exitPreview = () => { localStorage.removeItem(PREVIEW_KEY); window.location.href = window.location.pathname; };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, dir, isRTL, ready, preview, exitPreview }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage outside provider');
  return ctx;
}
